import ApiService, { type RequestOptions } from '@/services/api.service'
import { currentDateInUTCString } from '@/services/date.service'

import type { AxiosResponse } from 'axios'
import type {
  AggregatedClientFeesRequest,
  AggregatedClientFeesResponse,
  AggregatedFeesRequest,
  AggregatedFeesResponse,
  ClientTransfersResponse,
  FeeWithdrawalParams,
  FeesOverviewParams,
  FeesOverviewResponse,
  FeesStatsParams,
  FeesTotalsResponse,
  FeeWithdrawalsResponse,
  FirstDepositDatesResponse,
  FeesStatsResponse,
  PortfolioTransfersResponse,
  ReferralFeeTransfersResponse,
  RootUserTransfersRequest,
  RootUserTransfersResponse,
  TransferRequest,
  TransferResponse,
  AdminClientTransfersResponse,
} from '@/types/services/transfers'
import type { EmptyDataResponse } from '@/types/services/common'

class TransferService {
  /**
   * Create Transfer
   **/
  static createTransfer(
    data: TransferRequest
  ): Promise<AxiosResponse<TransferResponse>> {
    return ApiService.post('/api/v1/transfers', {
      ...data,
      request_timestamp: currentDateInUTCString(),
    })
  }

  /**
   * Update Transfer
   **/
  static updateTransfer(
    transferId: string,
    data: TransferRequest
  ): Promise<AxiosResponse<TransferResponse>> {
    return ApiService.put(`/api/v1/transfers/${transferId}`, data)
  }

  /**
   * Delete Transfer
   **/
  static deleteTransfer(
    transferId: string
  ): Promise<AxiosResponse<EmptyDataResponse>> {
    /**
     *  Args
     *    transferId (str): id of transfer
     **/
    return ApiService.delete(`/api/v1/transfers/${transferId}`)
  }

  static getFeesOverview(
    params: FeesOverviewParams
  ): Promise<AxiosResponse<FeesOverviewResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_fees_overview', {
      aggr: {
        params,
      },
    })
  }

  /**
   * Get all transfers info for given root user
   **/
  static getRootUser(
    data: RootUserTransfersRequest
  ): Promise<AxiosResponse<RootUserTransfersResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_root_user_transfers', {
      aggr: data,
    })
  }

  static getPortfolioTransfers(
    portfolio_id: string,
    currencies: string[]
  ): Promise<AxiosResponse<PortfolioTransfersResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_portfolio_transfers', {
      aggr: {
        params: {
          portfolio_id,
          currencies,
        },
      },
    })
  }

  /**
   * Get all clients transfers
   */
  static getAllClientsTransfers(
    fundProductId: string,
    currencies: string[]
  ): Promise<AxiosResponse<AdminClientTransfersResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_fund_dws', {
      aggr: {
        params: {
          fund_product_id: fundProductId,
          currencies: currencies,
        },
      },
    })
  }

  /**
   * Get transfers attributed to clients visible in the CRM table
   */
  static getAllClientsTransfersForClientsTable(
    fundProductId: string,
    currencies: string[]
  ): Promise<AxiosResponse<AdminClientTransfersResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_fund_dws_for_clients_table',
      {
        aggr: {
          params: {
            fund_product_id: fundProductId,
            currencies: currencies,
          },
        },
      }
    )
  }

  /**
   * Get client's transfers
   */
  static getClientTransfers(
    fundProductId: string,
    userEntityId: string,
    currencies: string[],
    options?: RequestOptions
  ): Promise<AxiosResponse<ClientTransfersResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_client_dws_for_client_view',
      {
        aggr: {
          params: {
            fund_product_id_: fundProductId,
            user_entity_id: userEntityId,
            currencies: currencies,
          },
        },
      },
      options
    )
  }

  /**
   * Get client's transfers with information about fee
   */
  static getClientTransfersForAdmin(
    fundProductId: string,
    userEntityId: string,
    currencies: string[],
    include_share_changes?: boolean
  ): Promise<AxiosResponse<AdminClientTransfersResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_client_dws_for_admin_view',
      {
        aggr: {
          params: {
            fund_product_id_: fundProductId,
            user_entity_id: userEntityId,
            currencies: currencies,
            include_share_changes,
          },
        },
      }
    )
  }

  /**
   * Get first deposit dates for entity or entity with specified user (client of fund)
   **/
  static getFirstDepositDates(
    parent_entity_id: string,
    user_entity_id: string | null,
    with_main_leading_series?: boolean,
    options?: RequestOptions
  ): Promise<AxiosResponse<FirstDepositDatesResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_first_deposit_dates',
      {
        aggr: {
          params: {
            parent_entity_id,
            user_entity_id,
            with_main_leading_series,
          },
        },
      },
      options
    )
  }

  static getFeesTotal(
    fundProductId: string,
    currencyId: string
  ): Promise<AxiosResponse<FeesTotalsResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_fees_total', {
      aggr: {
        params: {
          fund_product_id_: fundProductId,
          currency_id: currencyId,
        },
      },
    })
  }

  static getAggregatedFees(
    params: AggregatedFeesRequest
  ): Promise<AxiosResponse<AggregatedFeesResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_aggregated_fees', {
      aggr: {
        params,
      },
    })
  }

  static getAggregatedClientFees(
    params: AggregatedClientFeesRequest
  ): Promise<AxiosResponse<AggregatedClientFeesResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_aggregated_client_fees',
      {
        aggr: {
          params,
        },
      }
    )
  }

  static getFeeWithdrawals(
    fund_product_id: string,
    currencies: string[]
  ): Promise<AxiosResponse<FeeWithdrawalsResponse>> {
    return ApiService.post('/api/v1/transfer/search/get_fee_withdrawals', {
      aggr: {
        params: {
          fund_product_id_: fund_product_id,
          currencies,
        },
      },
    })
  }

  static getReferralFeeTransfers(
    fund_product_id: string,
    client_entity_id: string,
    currencies: string[],
    options?: RequestOptions
  ): Promise<AxiosResponse<ReferralFeeTransfersResponse>> {
    return ApiService.post(
      '/api/v1/transfer/search/get_referral_fee_transfers',
      {
        aggr: {
          params: {
            fund_product_id: fund_product_id,
            client_entity_id,
            currencies,
          },
        },
      },
      options
    )
  }

  static getFeesStats(
    params: FeesStatsParams
  ): Promise<AxiosResponse<FeesStatsResponse>> {
    const url = params.root_user_id
      ? '/api/v1/transfer/search/get_referrer_fees_stats'
      : '/api/v1/transfer/search/get_fund_fees_stats'

    return ApiService.post(url, {
      aggr: {
        params,
      },
    })
  }

  static createFundFeeWithdrawal(
    params: FeeWithdrawalParams
  ): Promise<AxiosResponse> {
    return ApiService.post(
      '/api/v1/transfer/search/create_fund_fee_withdrawal',
      {
        aggr: {
          params,
        },
        request_timestamp: currentDateInUTCString(),
      }
    )
  }

  static createReferrerFeeWithdrawal(
    params: FeeWithdrawalParams
  ): Promise<AxiosResponse> {
    return ApiService.post(
      '/api/v1/transfer/search/create_referrer_fee_withdrawal',
      {
        aggr: {
          params,
        },
        request_timestamp: currentDateInUTCString(),
      }
    )
  }

  static updateFundFeeWithdrawal(
    withdrawal_id: string,
    params: FeeWithdrawalParams
  ): Promise<AxiosResponse> {
    return ApiService.post(
      '/api/v1/transfer/search/update_fund_fee_withdrawal',
      {
        aggr: {
          params: {
            ...params,
            model_id: withdrawal_id,
          },
        },
      }
    )
  }

  static updateReferrerFeeWithdrawal(
    withdrawal_id: string,
    params: FeeWithdrawalParams
  ): Promise<AxiosResponse> {
    return ApiService.post(
      '/api/v1/transfer/search/update_referrer_fee_withdrawal',
      {
        aggr: {
          params: {
            ...params,
            model_id: withdrawal_id,
          },
        },
      }
    )
  }
}

export default TransferService
